import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { LutienApi } from '@src/services/lutien/lutien-api';
import OrgConfigView, { type OrgConfigForm, type ExtensionOption } from './OrgConfigView.tsx';
import { OrgConfigApi } from '@src/services/org-config/org-config-api';
import type { OrgConfigUpsertBodyDTO, OrgConfigGetResponseDTO } from '../../services/org-config/dto.ts';
import { HubApi } from '@src/services/hub/hub-api.ts';

const AVAILABLE_EXTENSIONS =
  process.env.NODE_ENV === 'dev'
    ? []
    : [
        { id: '41d38c22-dcb8-49e5-97bd-b84cbcd50b0f', label: 'Middleware' },
        { id: 'e621f7bc-a5fa-43dc-962d-deb9a8950f50', label: 'PDF Receivables' },
      ];

export const OrgConfigContainer = () => {
  const navigate = useNavigate();
  const hubApi = useMemo(() => new HubApi(), []);
  const lutienApi = useMemo(() => new LutienApi(), []);
  const orgConfigApi = useMemo(() => new OrgConfigApi(), []);

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [notConfiguredOpen, setNotConfiguredOpen] = useState(false);
  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);
  const [saveErrorOpen, setSaveErrorOpen] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | undefined>(undefined);
  const [isTogglingExtension, setIsTogglingExtension] = useState(false);
  const [extensionToastOpen, setExtensionToastOpen] = useState(false);
  const [extensionToastMessage, setExtensionToastMessage] = useState('');
  const [extensionToastSeverity, setExtensionToastSeverity] = useState<'success' | 'error'>('success');

  const { data: organizations, isLoading: isLoadingOrgs } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => lutienApi.v1.organizations.get(),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch status for all available extensions
  const {
    data: extensionsWithStatus,
    isLoading: isLoadingExtensions,
    refetch: refetchExtensions,
  } = useQuery({
    queryKey: ['extensions-status', selectedOrgId],
    queryFn: async () => {
      const extensionsData: ExtensionOption[] = await Promise.all(
        AVAILABLE_EXTENSIONS.map(async (ext) => {
          try {
            const data = await hubApi.v1.extensions.getByIdOrName({ idOrName: ext.id });
            return { ...ext, data };
          } catch (error) {
            console.error(`Failed to fetch status for extension ${ext.id}:`, error);
            return ext;
          }
        }),
      );
      return extensionsData;
    },
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: existingConfig,
    isLoading: isLoadingConfig,
    refetch,
  } = useQuery<OrgConfigGetResponseDTO | null>({
    queryKey: ['org-config', selectedOrgId],
    queryFn: async () => {
      if (!selectedOrgId) return null;
      try {
        return await orgConfigApi.v1.credentials.get({ organizationId: selectedOrgId });
      } catch (e) {
        if (e instanceof Error && e.message.toLowerCase().includes('status code 404')) {
          setNotConfiguredOpen(true);
        }
        return null;
      }
    },
    enabled: !!selectedOrgId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: false,
  });

  const upsertMutation = useMutation({
    mutationKey: ['org-config-upsert'],
    mutationFn: async (params: { organizationId: string; body: OrgConfigUpsertBodyDTO }) => {
      await orgConfigApi.v1.credentials.upsert(params);
    },
    onSuccess: () => {
      setSaveErrorOpen(false);
      setSaveErrorMessage(undefined);
      setSaveSuccessOpen(true);
      refetch();
    },
    onError: (e: unknown) => {
      const message = e instanceof Error ? e.message : 'Erro desconhecido ao salvar as credenciais.';
      setSaveErrorMessage(message);
      setSaveSuccessOpen(false);
      setSaveErrorOpen(true);
    },
  });

  const handleSelectOrg = (orgId: string) => {
    setNotConfiguredOpen(false);
    setSelectedOrgId(orgId);
  };

  const handleCloseNotConfigured = () => setNotConfiguredOpen(false);
  const handleCloseSaveSuccess = () => setSaveSuccessOpen(false);
  const handleCloseSaveError = () => setSaveErrorOpen(false);

  const handleSubmit = (form: OrgConfigForm) => {
    const bodyCandidate: OrgConfigUpsertBodyDTO = {
      token: form.token,
      aliareUrl: form.aliareUrl,
      aliareUser: form.aliareUser,
      aliareToken: form.aliareToken,
      aliareCode: form.aliareCode,
      withAssets: form.withAssets,
      targetPhaseId: form.targetPhaseId,
      organizationName: form.organizationName,
      considerCorporateName: form.considerCorporateName,
      defaultAssignedUserId: form.defaultAssignedUserId,
      aliareCycleUrl: form.aliareCycleUrl,
      invisibleCompanyBranch: form.invisibleCompanyBranch,
      withCycles: form.withCycles,
      allowedCreditLimitStatus: form.allowedCreditLimitStatus,
    };

    const body: OrgConfigUpsertBodyDTO = Object.fromEntries(
      Object.entries(bodyCandidate).filter(([key, value]) => {
        if (key === 'allowedCreditLimitStatus') return true;
        if (typeof value === 'string') return value.trim() !== '';
        return value !== undefined;
      }),
    ) as OrgConfigUpsertBodyDTO;

    if (import.meta.env.DEV)
      console.log('[OrgConfig] upsert.submit', {
        organizationId: form.organizationId,
        bodyKeys: Object.keys(body ?? {}),
      });

    upsertMutation.mutate({ organizationId: form.organizationId, body });
  };

  const handleGenerateToken = () => {
    if (crypto && 'randomUUID' in crypto && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    const bytes = new Uint8Array(16);
    if (crypto && typeof crypto.getRandomValues === 'function') {
      crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    const hex = Array.from(bytes, toHex).join('');
    return `${hex.substring(0, 8)}-${hex.substring(8, 12)}-${hex.substring(12, 16)}-${hex.substring(16, 20)}-${hex.substring(20)}`;
  };

  const handleGoToMapping = (organizationId: string) => {
    navigate(`/org-config/aliare-mapping?organizationId=${organizationId}`);
  };

  const handleExtensionsChange = async (newExtensions: ExtensionOption[]) => {
    if (!selectedOrgId || !extensionsWithStatus) return;

    setIsTogglingExtension(true);
    let hasErrors = false;
    let successCount = 0;
    const operations: string[] = [];

    try {
      const currentExtensions = extensionsWithStatus.filter((ext) => {
        if (!ext.data?.subscribedOrganizationIds) return false;
        return ext.data.subscribedOrganizationIds.includes(selectedOrgId);
      });

      const currentIds = new Set(currentExtensions.map((e) => e.id));
      const newIds = new Set(newExtensions.map((e) => e.id));

      // Extensões que foram adicionadas
      const toAdd = newExtensions.filter((e) => !currentIds.has(e.id));
      // Extensões que foram removidas
      const toRemove = currentExtensions.filter((e) => !newIds.has(e.id));

      // Processar adições
      for (const ext of toAdd) {
        try {
          await hubApi.v1.extensions.postSubscriptionById({
            id: ext.id,
            body: { organizationId: selectedOrgId },
          });
          successCount++;
          operations.push(`ativada: ${ext.label}`);
        } catch (error) {
          console.error(`Failed to subscribe extension ${ext.id}:`, error);
          hasErrors = true;
          setExtensionToastMessage(`Erro ao ativar extensão ${ext.label}`);
          setExtensionToastSeverity('error');
          setExtensionToastOpen(true);
        }
      }

      // Processar remoções
      for (const ext of toRemove) {
        try {
          await hubApi.v1.extensions.deleteSubscriptionById({
            id: ext.id,
            organizationId: selectedOrgId,
          });
          successCount++;
          operations.push(`desativada: ${ext.label}`);
        } catch (error) {
          console.error(`Failed to unsubscribe extension ${ext.id}:`, error);
          hasErrors = true;
          setExtensionToastMessage(`Erro ao desativar extensão ${ext.label}`);
          setExtensionToastSeverity('error');
          setExtensionToastOpen(true);
        }
      }

      // Refetch após todas as operações
      await refetchExtensions();

      // Mostrar mensagem de sucesso se houver operações bem-sucedidas e sem erros
      if (successCount > 0 && !hasErrors) {
        const message =
          operations.length === 1
            ? `Extensão ${operations[0]}`
            : `${successCount} extensão(ões) foram atualizadas com sucesso`;
        setExtensionToastMessage(message);
        setExtensionToastSeverity('success');
        setExtensionToastOpen(true);
      }
    } finally {
      setIsTogglingExtension(false);
    }
  };

  const handleCloseExtensionToast = () => {
    setExtensionToastOpen(false);
  };

  return (
    <OrgConfigView
      organizations={organizations ?? []}
      isLoadingOrgs={isLoadingOrgs}
      existingConfig={existingConfig}
      isLoadingConfig={isLoadingConfig}
      extensionOptions={extensionsWithStatus ?? AVAILABLE_EXTENSIONS}
      isLoadingExtensions={isLoadingExtensions}
      isTogglingExtension={isTogglingExtension}
      notConfiguredOpen={notConfiguredOpen}
      onCloseNotConfigured={handleCloseNotConfigured}
      isSaving={upsertMutation.isPending}
      saveSuccessOpen={saveSuccessOpen}
      onCloseSaveSuccess={handleCloseSaveSuccess}
      saveErrorOpen={saveErrorOpen}
      saveErrorMessage={saveErrorMessage}
      onCloseSaveError={handleCloseSaveError}
      extensionToastOpen={extensionToastOpen}
      extensionToastMessage={extensionToastMessage}
      extensionToastSeverity={extensionToastSeverity}
      onCloseExtensionToast={handleCloseExtensionToast}
      onSelectOrg={handleSelectOrg}
      onSubmit={handleSubmit}
      onGenerateToken={handleGenerateToken}
      onGoToMapping={handleGoToMapping}
      onExtensionsChange={handleExtensionsChange}
    />
  );
};

export default OrgConfigContainer;
